import React, {useState} from 'react';
import {BaseInputProps} from '../..';

import {Container, Label, Button, Placeholder, ContainerLabel} from './styles';
import {Popover, Tooltip} from '@mantine/core';
import {ErrorOption, useController, useFormContext} from 'react-hook-form';
import DatePickerComponent from 'shared/components/date-picker';
import dateFnsService from 'shared/services/date-fns-service';
import {NestedKeys} from 'shared/types/key-manipulation';
import ErrorWrapper from '../error-wrapper';
import {RiInformationFill} from 'react-icons/ri';

export interface DatePickerProps<T = any> extends BaseInputProps {
  placeholder?: string;
  gridColumn?: number;
  name: NestedKeys<T>;
}

const DatePicker: React.FC<DatePickerProps> = ({
  label,
  placeholder,
  gridColumn,
  name,
  border,
  disabled,
  required,
  information,
}) => {
  const {control, clearErrors, formState} = useFormContext();
  const {field} = useController({control, name});
  const [opened, setOpened] = useState(false);

  const error: ErrorOption | undefined = formState.errors
    ? formState.errors[name as string]
    : undefined;

  const handleDateChange = (date: Date) => {
    clearErrors();
    const formattedDate = dateFnsService.usDateFormat(date);
    setOpened(false);
    field.onChange(formattedDate);
  };

  return (
    <Popover
      {...{opened}}
      position="bottom-start"
      onClose={() => setOpened(false)}
      offset={{crossAxis: 10, mainAxis: -10}}
      styles={{dropdown: {padding: 0}}}
      {...{disabled}}>
      <Popover.Target>
        <div
          style={{
            gridColumn: `span ${gridColumn ?? 12}`,
          }}>
          <ErrorWrapper {...{error: error?.message, gridColumn}}>
            <Container
              {...{gridColumn, border, focused: opened, error}}
              onClick={() => !disabled && setOpened(true)}>
              <ContainerLabel>
                {label && (
                  <Label {...{error: !!error}}>
                    {label}
                    {required && ' *'}
                  </Label>
                )}
                {information && (
                  <Tooltip
                    label={information}
                    multiline
                    withArrow
                    arrowSize={10}
                    openDelay={500}
                    w={200}
                    position="top">
                    <div>
                      <RiInformationFill size={20} />
                    </div>
                  </Tooltip>
                )}
              </ContainerLabel>
              {!field.value && (
                <Placeholder {...{error: !!error}}>{placeholder}</Placeholder>
              )}
              {field.value && (
                <Button {...{error: !!error}}>
                  {dateFnsService.usDateToDateFormat(field.value)}
                </Button>
              )}
            </Container>
          </ErrorWrapper>
        </div>
      </Popover.Target>
      <Popover.Dropdown>
        <DatePickerComponent confirm={handleDateChange} />
      </Popover.Dropdown>
    </Popover>
  );
};

export default DatePicker;
